import { Skeleton } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { WizardButton, WizardField, WizardFooter, WizardIntro, WizardSelect } from '../../ui';
import { useCompany } from '../api';
import { useCompanyForm } from '../companyForm';
import { LoadError } from '../LoadError';
import type { Company } from '../types';
import { useWizardNav } from '../wizardContext';
import { stepEyebrow } from '../wizardSteps';
import { useState } from 'react';
import styles from './steps.module.css';

/** Onboarding Admin 04 · Firmendaten: eight fields with auto-save, the MIAS check of the VAT ID. */
export function CompanyStep() {
  const company = useCompany();
  return (
    <>
      <WizardIntro
        eyebrow={stepEyebrow('company')}
        title="Firmendaten"
        text="Diese Angaben stehen im Impressum, auf Rechnungen und in Mails an Mandanten. Sie werden auch für die Twilio-Prüfung gebraucht."
      />
      {company.isPending ? (
        <div className={styles.skeletonStack}>
          <Skeleton variant="block" height={420} />
        </div>
      ) : company.isError ? (
        <LoadError
          what="Die Firmendaten konnten nicht geladen werden"
          error={company.error}
          retrying={company.isFetching}
          onRetry={() => void company.refetch()}
        />
      ) : (
        <CompanyForm company={company.data} />
      )}
    </>
  );
}

function CompanyForm({ company }: { company: Company }) {
  const nav = useWizardNav();
  const form = useCompanyForm(company);
  const [pending, setPending] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const status = company.vatCheck.status;
  const vatDirty = form.values.vatId.trim() !== (company.vatId ?? '');
  const vatInvalid = !vatDirty && status === 'invalid';
  const vatConfirmed = !vatDirty && status === 'valid';

  async function next() {
    setPending(true);
    setStepError(null);
    try {
      await form.settle();
      await nav.complete('company_data', 'continue', 'legal-texts');
    } catch (error) {
      setStepError(
        error instanceof ApiError ? (error.fields['company'] ?? error.message) : errorMessage(error),
      );
    } finally {
      setPending(false);
    }
  }

  const text = (
    field:
      'companyName' | 'street' | 'postalCodeCity' | 'register' | 'managingDirectors' | 'supervisoryAuthority',
  ) => ({
    value: form.values[field],
    onChange: (event: { target: { value: string } }) => form.set(field, event.target.value),
    onBlur: () => void form.commit(field),
    error: form.errors[field],
  });

  return (
    <>
      <div className={styles.companyCard}>
        <div className={styles.companyGrid}>
          <WizardField
            label="Firmenname laut Handelsregister"
            autoComplete="organization"
            {...text('companyName')}
          />
          <WizardSelect
            label="Rechtsform"
            value={form.values.legalForm}
            options={[
              ...(form.values.legalForm === '' ? [{ value: '', label: 'Bitte wählen' }] : []),
              ...company.legalForms.map((option) => ({ value: option, label: option })),
            ]}
            onChange={(event) => {
              form.set('legalForm', event.target.value);
              void form.commit('legalForm', event.target.value);
            }}
            error={form.errors.legalForm}
          />
          <WizardField label="Straße und Hausnummer" autoComplete="street-address" {...text('street')} />
          <WizardField label="PLZ und Ort" numeric {...text('postalCodeCity')} />
          <WizardField label="Registergericht und Nummer" {...text('register')} />
          <WizardField
            label={
              vatInvalid || status === 'unavailable'
                ? 'Umsatzsteuer-ID · nicht bestätigt'
                : vatConfirmed
                  ? 'Umsatzsteuer-ID · bestätigt'
                  : 'Umsatzsteuer-ID'
            }
            numeric
            value={form.values.vatId}
            onChange={(event) => form.set('vatId', event.target.value)}
            onBlur={() => void form.commit('vatId')}
            error={form.errors.vatId ?? (vatInvalid ? company.vatCheck.label : undefined)}
            hint={
              !vatDirty && (status === 'unavailable' || status === 'valid')
                ? company.vatCheck.label
                : undefined
            }
          />
          <WizardField label="Geschäftsführung" {...text('managingDirectors')} />
          <WizardField label="Aufsichtsbehörde · § 34c GewO" {...text('supervisoryAuthority')} />
        </div>
        <div className={styles.companyHintRow}>
          <span className={styles.companyHint}>
            Zentrale Telefonnummer und Mailadresse folgen im Telefonie-Schritt.
          </span>
          <WizardButton
            variant="secondary"
            size="small"
            className={styles.push}
            loading={form.checking}
            onClick={() => void form.checkVat()}
          >
            USt-ID erneut prüfen
          </WizardButton>
        </div>
      </div>
      {form.failure || stepError ? (
        <div className={styles.error} role="alert">
          {stepError ?? form.failure}
        </div>
      ) : null}
      <WizardFooter
        back={{ label: 'Zurück', onClick: () => nav.go('brand') }}
        hint={vatConfirmed ? undefined : 'Ohne bestätigte USt-ID wird mit Umsatzsteuer abgerechnet.'}
        primary={{
          label: vatConfirmed ? 'Weiter' : 'Trotzdem weiter',
          loading: pending,
          onClick: () => void next(),
        }}
      />
    </>
  );
}
